/**
 * CalendarDayCell Component
 *
 * Individual day cell with visual states for the date range picker.
 */

import React from 'react';
import type { DayStatus } from './useDateRangePicker';

interface CalendarDayCellProps {
  date: Date;
  status: DayStatus;
  onClick: (date: Date) => void;
  onHover: (date: Date | null) => void;
}

const statusStyles: Record<DayStatus, string> = {
  normal: 'text-gray-900 hover:border-gray-400 cursor-pointer',
  disabled: 'text-gray-300 cursor-default',
  blocked: 'text-gray-400 line-through cursor-default',
  'check-in': 'bg-gray-900 text-white rounded-full cursor-pointer',
  'check-out': 'bg-gray-900 text-white rounded-full cursor-pointer',
  'in-range': 'bg-gray-100 text-gray-900 cursor-pointer',
  'hover-preview': 'bg-gray-50 text-gray-700 cursor-pointer',
};

export const CalendarDayCell: React.FC<CalendarDayCellProps> = ({
  date,
  status,
  onClick,
  onHover,
}) => {
  const isInteractive = status !== 'disabled' && status !== 'blocked';
  const day = date.getDate();

  // Range background strips (continuous between check-in and check-out)
  const isRangeMiddle = status === 'in-range' || status === 'hover-preview';
  const isCheckIn = status === 'check-in';
  const isCheckOut = status === 'check-out';

  return (
    <td className="p-0">
      <div
        className={`relative ${isRangeMiddle ? 'bg-gray-100' : ''} ${
          isCheckIn ? 'bg-gradient-to-r from-transparent to-gray-100 rounded-l-none' : ''
        } ${
          isCheckOut ? 'bg-gradient-to-l from-transparent to-gray-100 rounded-r-none' : ''
        }`}
      >
        <button
          type="button"
          disabled={!isInteractive}
          onClick={() => isInteractive && onClick(date)}
          onMouseEnter={() => isInteractive && onHover(date)}
          onMouseLeave={() => onHover(null)}
          className={`w-full aspect-square flex items-center justify-center text-sm font-medium border border-transparent transition-colors ${statusStyles[status]}`}
        >
          {day}
        </button>
      </div>
    </td>
  );
};

export default CalendarDayCell;
